// import builderValidationSchema from './buildValidationSchema';
import groupBy from 'lodash/groupBy';
import get from 'lodash/get';
import find from 'lodash/find';
import cond from 'lodash/cond';
import toNumber from 'lodash/toNumber';
import isNil from 'lodash/isNil';

import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import isEqual from 'date-fns/isEqual';
import parse from 'date-fns/parse';
import format from 'date-fns/format';

import { LOGIC_PROPERTY_REQUIRED } from 'common/entities/formLogicRule/constants';
import {
    INPUT_TYPE_ADDRESS,
    INPUT_TYPE_CHECKBOX,
    INPUT_TYPE_DATE,
    INPUT_TYPE_TIME,
    INPUT_TYPE_EMAIL,
    INPUT_TYPE_HIDDEN,
    INPUT_TYPE_NUMBER,
    INPUT_TYPE_PARAGRAPH,
    INPUT_TYPE_PHONE_NUMBER,
    INPUT_TYPE_TEXT,
    INPUT_TYPE_URL,
    INPUT_TYPE_SELECT,
} from 'common/entities/formNode/inputTypes';
import { NODE_TYPE_QUESTION, NODE_TYPE_QUESTION_MULTIPLE } from 'common/entities/formNode/nodeTypes';
import {
    NODE_NAME_DATE_RANGE_END_DATE,
    NODE_NAME_DATE_RANGE_END_TIME,
    NODE_NAME_DATE_RANGE_START_DATE,
    NODE_NAME_DATE_RANGE_START_TIME,
    NODE_NAME_SOCIAL_RSVP,
} from 'common/entities/formNode/nodeNames';
import { ERROR_INVALID_FORMAT, ERROR_REQUIRED } from 'common/entities/form/errorMessages';
import { NUMBER_MAX, NUMBER_MIN, USE_BROWSER_TIME } from 'common/entities/formNode/nodeConfig';

import { isValidEmail } from 'utils/textFormats';

import getNodesVisibility from './getNodesVisibility';
import { getDateRangeEffectiveLimits } from './dateRanges';

const FILL_DATE_RANGE_ERROR_LABEL = 'Please, fill all the date range values.';
export const INVALID_START_DATE_RANGE_ERROR_LABEL = 'Start date should be before end date.';
export const INVALID_END_DATE_RANGE_ERROR_LABEL = 'End date should be after start date.';
export const INVALID_START_TIME_RANGE_ERROR_LABEL = 'Start time should be before end time.';
export const INVALID_END_TIME_RANGE_ERROR_LABEL = 'End time should be after start time.';
const INVALID_DATE_ERROR_LABEL = 'Please enter the date in the MM/DD/YYYY format.';
const INVALID_TIME_MINIMUM = `Time must be after ${format(new Date(), 'hh:mma')}`;

/**
 * Generates a context-aware error message for date question validation failures
 * Provides specific guidance based on date constraints in a consistent format.
 * WCAG 3.3.3 Error Suggestion: Reminds users of the expected constraint and how to fix it.
 * @param  {Object} node - form node with config containing date constraints
 * @return {string} error message
 */
export const getDateQuestionErrorLabel = (node) => {
    const { before, after } = getDateRangeEffectiveLimits(node.config);

    // Create and format Date objects once
    const beforeDate = before ? new Date(before) : null;
    const afterDate = after ? new Date(after) : null;

    // Validate dates before formatting to prevent RangeError on invalid dates
    const isValidDate = (d) => d instanceof Date && !Number.isNaN(d.getTime());
    const beforeFormatted = isValidDate(beforeDate) ? format(beforeDate, 'MMM d, yyyy') : null;
    const afterFormatted = isValidDate(afterDate) ? format(afterDate, 'MMM d, yyyy') : null;

    // Only upper limit: provide the upper bound
    if (!beforeFormatted && afterFormatted) {
        return `Please select a date on or before ${afterFormatted}.`;
    }

    // Only lower limit: provide the lower bound
    if (beforeFormatted && !afterFormatted) {
        return `Please select a date on or after ${beforeFormatted}.`;
    }

    // Both limits: ensure chronological order (earliest to latest)
    if (beforeFormatted && afterFormatted) {
        const [startDate, endDate] = isBefore(beforeDate, afterDate)
            ? [beforeFormatted, afterFormatted]
            : [afterFormatted, beforeFormatted];
        return `Please select a date between ${startDate} and ${endDate}.`;
    }

    return INVALID_DATE_ERROR_LABEL;
};

export const FIRST_NAME_INPUT_NAME = 'first-name';
export const LAST_NAME_INPUT_NAME = 'last-name';
export const INVALID_FIRST_NAME_ERROR = 'Add a space between "First Name" initials. Example: F. N.';
export const INVALID_LAST_NAME_ERROR = 'Add a space between "Last Name" initials. Example: F. N.';

const groupRulesByTarget = (formLogicRules) => groupBy(formLogicRules, 'target');
const groupRulesByProperty = (formLogicRules) => groupBy(formLogicRules, 'property');
const hasRuleRequired = (formLogicRules) =>
    find(formLogicRules, (formLogicRule) => get(formLogicRule, 'defaultValue', false));
const filterQuestionNodes = (formNodes) => formNodes.filter((formNode) => formNode.type === NODE_TYPE_QUESTION);
const filterQuestionMultipleNodes = (formNodes) =>
    formNodes.filter((formNode) => formNode.type === NODE_TYPE_QUESTION_MULTIPLE);

/**
 * Checks if an url node matches the given regexp.
 * @param  {string} value
 * @return {boolean}
 */
const matchesUrlFormat = (value) => {
    const regex = new RegExp(/^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w-._~:/?#[\]@!$&'()*+,;=.]+$/, 'g');
    return regex.test(value);
};
const containsUrl = (value) => {
    const regex = new RegExp(
        /([a-zA-Z0-9]+:\/\/)?(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w-._~:/?#[\]@!$&'()*+,;=.]/,
        'g',
    );
    return regex.test(value);
};
const matchesMailtoFormat = (value) => {
    const regex = new RegExp(/^mailto:[^@\s]+@[^.\s]+\.[^\s@,]/, 'g');
    return regex.test(value);
};
/**
 * Determines if the required node accepts the value
 * @param  {Object} node
 * @param  {*} value
 * @return {boolean}
 */
export const isRequiredValid = (node, value) => {
    if (value == null) {
        return false;
    }
    switch (node.inputType) {
        case INPUT_TYPE_TEXT:
        case INPUT_TYPE_EMAIL:
        case INPUT_TYPE_DATE:
        case INPUT_TYPE_PHONE_NUMBER:
        case INPUT_TYPE_URL:
        case INPUT_TYPE_NUMBER:
        case INPUT_TYPE_SELECT:
        case INPUT_TYPE_PARAGRAPH:
            return typeof value === 'string' && value.trim() !== '';
        case INPUT_TYPE_ADDRESS:
            return get(value, 'label', '') !== '' || value !== '';
        case INPUT_TYPE_CHECKBOX:
            return value !== '0' && value !== 0 && value !== false;
        case INPUT_TYPE_HIDDEN:
            return true;
        default:
            return true;
    }
};

/**
 * @param  {Array} formNodes - array of entities
 * @param  {Array} formLogicRules - array of entities
 * @param  {Object} values - { [nodeId]: value, ... }
 * @return {Array} - subset of formNodes
 */
const getVisibleNodes = (formNodes, formLogicRules, values) => {
    const nodesVisibility = getNodesVisibility(formNodes, formLogicRules, values);
    return formNodes.filter((formNode) => nodesVisibility[formNode.id]);
};

const isRequiredError = (node, value, logicRulesByTarget) => {
    const logicRulesByProperty = groupRulesByProperty(logicRulesByTarget[node.id]);
    const isRequired = hasRuleRequired(logicRulesByProperty[LOGIC_PROPERTY_REQUIRED]);

    return isRequired && !isRequiredValid(node, value);
};

const isInvalidFormatError = (node, value, _) => {
    const isUrlError = node.inputType === INPUT_TYPE_URL && isRequiredValid(node, value) && !matchesUrlFormat(value);
    const isEmailError = node.inputType === INPUT_TYPE_EMAIL && isRequiredValid(node, value) && !isValidEmail(value);
    return isUrlError || isEmailError;
};

const isNumberRangeError = (node, value) => {
    const min = get(node, ['config', NUMBER_MIN]);
    const max = get(node, ['config', NUMBER_MAX]);

    const isLowerThanMin = !isNil(min) && toNumber(value) < min;
    const isGreaterThanMax = !isNil(max) && toNumber(value) > max;

    return node.inputType === INPUT_TYPE_NUMBER && isRequiredValid(node, value) && (isLowerThanMin || isGreaterThanMax);
};

const getGroupValues = (parent, formNodes, values) =>
    formNodes
        .filter((node) => node.parent === parent)
        .map((node) => ({
            name: node.name,
            value: values[node.id],
            config: node.config,
        }));

const hasInvalidDate = (parent, formNodes, values) => {
    const dateRangeGroup = getGroupValues(parent, formNodes, values);

    const startDate = dateRangeGroup.find((node) => node.name === NODE_NAME_DATE_RANGE_START_DATE).value;
    const endDate = dateRangeGroup.find((node) => node.name === NODE_NAME_DATE_RANGE_END_DATE).value;

    const parsedStartDate = parse(startDate, 'MM/dd/yyyy', new Date(startDate));
    const parsedEndDate = parse(endDate, 'MM/dd/yyyy', new Date(endDate));

    return startDate && endDate && isAfter(parsedStartDate, parsedEndDate);
};

const hasInvalidTime = (parent, formNodes, values) => {
    const dateRangeGroup = getGroupValues(parent, formNodes, values);

    const startDate = dateRangeGroup.find((node) => node.name === NODE_NAME_DATE_RANGE_START_DATE).value;
    const endDate = dateRangeGroup.find((node) => node.name === NODE_NAME_DATE_RANGE_END_DATE).value;
    const startTime = dateRangeGroup.find((node) => node.name === NODE_NAME_DATE_RANGE_START_TIME).value;
    const endTime = dateRangeGroup.find((node) => node.name === NODE_NAME_DATE_RANGE_END_TIME).value;

    const parsedStartDate = parse(startDate, 'MM/dd/yyyy', new Date(startDate));
    const parsedEndDate = parse(endDate, 'MM/dd/yyyy', new Date(endDate));
    const parsedStartTime = parse(startTime, 'hh:mma', new Date());
    const parsedEndTime = parse(endTime, 'hh:mma', new Date());

    return (
        startTime &&
        endTime &&
        isEqual(parsedStartDate, parsedEndDate) &&
        (isEqual(parsedStartTime, parsedEndTime) || isAfter(parsedStartTime, parsedEndTime))
    );
};

const hasGroupFilledValues = (parent, formNodes, values) =>
    getGroupValues(parent, formNodes, values)
        .map(({ value }) => !!value)
        .includes(true);

const isDateRangeQuestion = (nodeName) => {
    const dateRangeQuestions = {
        [NODE_NAME_DATE_RANGE_START_DATE]: true,
        [NODE_NAME_DATE_RANGE_END_DATE]: true,
        [NODE_NAME_DATE_RANGE_START_TIME]: true,
        [NODE_NAME_DATE_RANGE_END_TIME]: true,
    };

    return !!dateRangeQuestions[nodeName];
};

const isDateRangeIncompleteError = (node, value, logicRulesByTarget, formNodes, values) =>
    isDateRangeQuestion(node.name) && hasGroupFilledValues(node.parent, formNodes, values) && !value;

const hasDateRangeError = {
    [NODE_NAME_DATE_RANGE_START_DATE]: (parent, formNodes, values) => hasInvalidDate(parent, formNodes, values),
    [NODE_NAME_DATE_RANGE_END_DATE]: (parent, formNodes, values) => hasInvalidDate(parent, formNodes, values),
    [NODE_NAME_DATE_RANGE_START_TIME]: (parent, formNodes, values) => hasInvalidTime(parent, formNodes, values),
    [NODE_NAME_DATE_RANGE_END_TIME]: (parent, formNodes, values) => hasInvalidTime(parent, formNodes, values),
};

const dateRangeErrorLabel = {
    [NODE_NAME_DATE_RANGE_START_DATE]: INVALID_START_DATE_RANGE_ERROR_LABEL,
    [NODE_NAME_DATE_RANGE_END_DATE]: INVALID_END_DATE_RANGE_ERROR_LABEL,
    [NODE_NAME_DATE_RANGE_START_TIME]: INVALID_START_TIME_RANGE_ERROR_LABEL,
    [NODE_NAME_DATE_RANGE_END_TIME]: INVALID_END_TIME_RANGE_ERROR_LABEL,
};

const isDateRangeInvalidError = (node, value, logicRulesByTarget, formNodes, values) =>
    hasDateRangeError[node.name] && hasDateRangeError[node.name](node.parent, formNodes, values);

const isDateQuestionRangeError = (node, value) => {
    const { before, after } = getDateRangeEffectiveLimits(node.config);
    return (
        (before && isBefore(new Date(value), new Date(before))) || (after && isAfter(new Date(value), new Date(after)))
    );
};

const getDateRangeStartDate = (allFormNodes, dateRangeNode) => {
    const groupNodeId = get(dateRangeNode, 'parent');
    const dateRangeFormNodes = allFormNodes.filter((node) => get(node, 'parent') === groupNodeId);
    return dateRangeFormNodes.find((node) => get(node, 'name') === NODE_NAME_DATE_RANGE_START_DATE);
};

export const isTimeQuestionRangeError = (node, value, logicRulesByTarget, formNodes, values) => {
    const useBrowserTime = get(node, `config.${USE_BROWSER_TIME}`, false);

    if (!useBrowserTime) return false;

    const dateRangeStartDate = getDateRangeStartDate(formNodes, node);
    const dateRangeStartDateId = get(dateRangeStartDate, 'id');
    const dateRangeStartDateValue = get(values, dateRangeStartDateId, null);
    const currentDate = format(new Date(), 'MM/dd/yyyy');
    const parsedCurrentDate = parse(currentDate, 'MM/dd/yyyy', new Date());
    const parsedStartDate = parse(dateRangeStartDateValue, 'MM/dd/yyyy', new Date());
    const isToday = isEqual(parsedCurrentDate, parsedStartDate);

    if (!isToday) return false;

    const browserCurrentTime = format(new Date(), 'hh:mma');
    const parsedCurrentTime = parse(browserCurrentTime, 'hh:mma', new Date());
    const parsedTimeValue = parse(value, 'hh:mma', new Date());
    return isAfter(parsedCurrentTime, parsedTimeValue);
};

const isDateError = (node, value) => node.inputType === INPUT_TYPE_DATE && isDateQuestionRangeError(node, value);

const isTimeMinError = (node, value, logicRulesByTarget, formNodes, values) =>
    node.inputType === INPUT_TYPE_TIME && isTimeQuestionRangeError(node, value, logicRulesByTarget, formNodes, values);

const isFirstNameError = (node, value) =>
    node.name === FIRST_NAME_INPUT_NAME && (containsUrl(value) || matchesMailtoFormat(value));

const isLastNameError = (node, value) =>
    node.name === LAST_NAME_INPUT_NAME && (containsUrl(value) || matchesMailtoFormat(value));

const getNumberRangeErrorLabel = cond([
    [(min, max) => !isNil(min) && !isNil(max), (min, max) => `This value must be between ${min} and ${max}.`],
    [(min, max) => !isNil(min) && isNil(max), (min) => `This value must be equal to or greater than ${min}.`],
    [(min, max) => isNil(min) && !isNil(max), (min, max) => `This value must be equal to or lower than ${max}.`],
]);

const getQuestionError = cond([
    [isRequiredError, () => ERROR_REQUIRED],
    [isInvalidFormatError, () => ERROR_INVALID_FORMAT],
    [
        isNumberRangeError,
        (node) => {
            const min = get(node, ['config', NUMBER_MIN]);
            const max = get(node, ['config', NUMBER_MAX]);

            return getNumberRangeErrorLabel(min, max);
        },
    ],
    [isDateRangeIncompleteError, () => FILL_DATE_RANGE_ERROR_LABEL],
    [isDateRangeInvalidError, (node) => dateRangeErrorLabel[node.name]],
    [isTimeMinError, () => INVALID_TIME_MINIMUM],
    [isDateError, (node) => getDateQuestionErrorLabel(node)],
    [isFirstNameError, () => INVALID_FIRST_NAME_ERROR],
    [isLastNameError, () => INVALID_LAST_NAME_ERROR],
]);

/**
 * @param  {Array} formNodes - array of entities
 * @param  {Array} formLogicRules - array of entities
 * @param  {Object} values - { [nodeId]: value], ... }
 * @return {Object} - errors. { [nodeId]: [errorMsg1, errorMsg2, ...], ... }
 */
export function validateQuestions(formNodes, formLogicRules, values) {
    const logicRulesByTarget = groupRulesByTarget(formLogicRules);

    return formNodes.reduce((memo, node) => {
        const nodeId = node.id;
        const value = values[nodeId];

        const error = getQuestionError(node, value, logicRulesByTarget, formNodes, values);

        return !error
            ? memo
            : {
                  ...memo,
                  [nodeId]: (memo.nodeId || []).concat(error),
              };
    }, {});
}

const getNodeMetaByName = (node, logicRulesByTarget, values) => {
    const metasByName = {
        [NODE_NAME_SOCIAL_RSVP]: () => {
            const logicRulesByProperty = groupRulesByProperty(logicRulesByTarget[node.id]);
            const value = values[node.id];

            const requiredError =
                hasRuleRequired(logicRulesByProperty[LOGIC_PROPERTY_REQUIRED]) && !isRequiredValid(node, value);

            return {
                requiredError,
            };
        },
        default: () => null,
    };

    return (metasByName[node.name] || metasByName.default)(node);
};

export function validateQuestionMultiple(formNodes, formLogicRules, values) {
    const logicRulesByTarget = groupRulesByTarget(formLogicRules);

    const errors = {};
    const meta = formNodes.reduce((acc, node) => {
        const nodeMeta = getNodeMetaByName(node, logicRulesByTarget, values);

        return nodeMeta
            ? {
                  ...acc,
                  [node.id]: nodeMeta,
              }
            : acc;
    }, {});

    return [errors, meta];
}

/**
 * @param  {Array} formNodes - array of entities, including nodes that might not be visible.
 * @param  {Array} formLogicRules - array of entities
 * @param  {Object} values - { [nodeId]: value], ... }
 * @return {Object} - errors. { [nodeId]: [errorMsg1, errorMsg2, ...], ... }
 */
export default function onSubmitValidate(formNodes, formLogicRules, values, isUpdating) {
    const visibleNodes = getVisibleNodes(formNodes, formLogicRules, values);
    const visibleNodesFiltered = isUpdating ? visibleNodes.filter((formNode) => !formNode.protected) : visibleNodes;
    const visibleQuestionNodes = filterQuestionNodes(visibleNodesFiltered);
    const visibleQuestionMultipleNodes = filterQuestionMultipleNodes(visibleNodesFiltered);
    const questionErrors = validateQuestions(visibleQuestionNodes, formLogicRules, values);
    const [questionMultipleErrors, questionMultipleMeta] = validateQuestionMultiple(
        visibleQuestionMultipleNodes,
        formLogicRules,
        values,
    );

    const errors = {
        ...questionErrors,
        ...questionMultipleErrors,
    };

    const meta = {
        ...questionMultipleMeta,
    };

    return [errors, meta /* TODO: , valid */];
}
